import type { FoodCategory, Market } from '../types';

export interface ProductSuggestion {
  sku: string;
  papel: 'lider' | 'complementar' | 'margem';
  cvb: string;
  whenToUse: string;
  scenarioSignals?: string[];
  goalFit?: string[];
}

export interface ExecutionPlaybook {
  priorities: string[];
  routine: string[];
  proof: string[];
}

export const CATEGORY_PRODUCT_SUGGESTIONS: Record<FoodCategory, ProductSuggestion[]> = {
  aves: [
    {
      sku: 'Filé de Peito em Bifes IQF 1kg',
      papel: 'lider',
      cvb: 'Cortes IQF, rastreabilidade e preparo versátil que transformam conveniência em giro recorrente no dia a dia.',
      whenToUse: 'Quando a conta precisa de item líder com alta cobertura de público e reposição simples.',
      scenarioSignals: ['giro', 'dia a dia', 'reposição', 'ruptura', 'conveniência'],
      goalFit: ['melhorar_execucao', 'abrir_espaco', 'aumentar_share']
    },
    {
      sku: 'Aprecialle',
      papel: 'margem',
      cvb: 'Ave especial festiva com preparo prático, apelo premium e potencial de ticket mais alto em datas sazonais.',
      whenToUse: 'Quando houver espaço para calendário sazonal, cestas, presentes ou linha premium.',
      scenarioSignals: ['sazonal', 'natal', 'fim de ano', 'presente', 'premium', 'calendário'],
      goalFit: ['ampliar_mix', 'introduzir_sku', 'ganhar_exposicao']
    },
    {
      sku: 'Raquete Temperada',
      papel: 'complementar',
      cvb: 'Produto pronto para assar que aumenta conveniência e cria ponte entre item tradicional e solução de maior valor.',
      whenToUse: 'Quando a categoria precisa crescer mix sem perder apelo de giro.',
      scenarioSignals: ['conveniência', 'praticidade', 'mix', 'ampliar', 'lançamento'],
      goalFit: ['ampliar_mix', 'introduzir_sku', 'ganhar_exposicao']
    }
  ],
  embutidos: [
    {
      sku: 'Linguiça de Frango Tradicional 1kg',
      papel: 'lider',
      cvb: 'Produto com amplo apelo, preparo fácil e marca forte para gerar recompra e reduzir risco de estoque parado.',
      whenToUse: 'Quando o cliente precisa de base segura de giro e presença na categoria.',
      scenarioSignals: ['churrasco', 'giro', 'volume', 'base', 'presença'],
      goalFit: ['abrir_espaco', 'aumentar_share', 'melhorar_execucao']
    },
    {
      sku: 'Linguiça Fininha',
      papel: 'complementar',
      cvb: 'Amplia ocasião de consumo e ajuda a sofisticar a gôndola sem romper a lógica de conveniência.',
      whenToUse: 'Quando houver oportunidade de ampliar variedade e ocasião de compra.',
      scenarioSignals: ['ocasião', 'variedade', 'mix', 'sofisticar', 'premium'],
      goalFit: ['ampliar_mix', 'introduzir_sku', 'ganhar_exposicao']
    },
    {
      sku: 'Linha de embutidos premium',
      papel: 'margem',
      cvb: 'Entrega diferenciação percebida e ajuda a trabalhar margem em reais com menor dependência de guerra de preço.',
      whenToUse: 'Quando o cliente aceita migrar parte da conversa para rentabilidade da categoria.',
      scenarioSignals: ['margem em reais', 'margem', 'premium', 'rentabilidade', 'valor agregado'],
      goalFit: ['defender_preco', 'ampliar_mix', 'fortalecer_parceria']
    }
  ],
  industrializados: [
    {
      sku: 'Filezinho Empanado 1kg',
      papel: 'margem',
      cvb: 'Produto premium, certificado e de alto valor agregado, com preparo rápido e boa margem de lucro.',
      whenToUse: 'Quando a conta busca conveniência com melhor rentabilidade.',
      scenarioSignals: ['premium', 'conveniência', 'margem', 'valor agregado', 'air fryer'],
      goalFit: ['defender_preco', 'ampliar_mix', 'introduzir_sku']
    },
    {
      sku: 'Steak',
      papel: 'lider',
      cvb: 'Categoria de conveniência com apelo massivo e facilidade operacional para ampliar presença e frequência de compra.',
      whenToUse: 'Quando o cliente precisa gerar volume com execução simples.',
      scenarioSignals: ['volume', 'execução', 'presença', 'frequência', 'giro'],
      goalFit: ['abrir_espaco', 'ganhar_exposicao', 'melhorar_execucao']
    },
    {
      sku: 'Chicken',
      papel: 'complementar',
      cvb: 'Ajuda a ampliar variedade e a capturar ocasiões de consumo adicionais com discurso de praticidade.',
      whenToUse: 'Quando o objetivo for ampliar mix com baixo atrito de adoção.',
      scenarioSignals: ['mix', 'variedade', 'ocasião', 'praticidade'],
      goalFit: ['ampliar_mix', 'introduzir_sku', 'aumentar_share']
    }
  ],
  pescados: [
    {
      sku: 'Filé de Tilápia',
      papel: 'margem',
      cvb: 'Produção própria, rastreabilidade e argumento premium que sustentam margem superior e percepção forte de qualidade.',
      whenToUse: 'Quando a conta aceita crescer em categoria premium ou diferenciar a gôndola.',
      scenarioSignals: ['premium', 'qualidade', 'rastreabilidade', 'segurança alimentar', 'margem'],
      goalFit: ['defender_preco', 'introduzir_sku', 'ganhar_exposicao']
    },
    {
      sku: 'Posta de Tilápia',
      papel: 'lider',
      cvb: 'Formato acessível com argumento de sabor, frescor e textura para ampliar presença da categoria.',
      whenToUse: 'Quando a categoria precisa ganhar base de giro.',
      scenarioSignals: ['giro', 'base', 'presença', 'categoria'],
      goalFit: ['abrir_espaco', 'aumentar_share', 'melhorar_execucao']
    },
    {
      sku: 'Petisco de Tilápia',
      papel: 'complementar',
      cvb: 'Ajuda a criar ocasião de consumo e a sofisticar o mix com proposta de conveniência.',
      whenToUse: 'Quando o cliente busca diferenciação e incremento de ticket.',
      scenarioSignals: ['ticket', 'diferenciação', 'mix', 'ocasião', 'conveniência'],
      goalFit: ['ampliar_mix', 'ganhar_exposicao', 'introduzir_sku']
    }
  ]
};

export const EXECUTION_BY_MARKET: Record<Market, ExecutionPlaybook> = {
  interno: {
    priorities: [
      'Checar frente mínima por SKU, etiqueta próxima e rótulo corretamente orientado.',
      'Verificar ruptura, estoque virtual, validade e limpeza do equipamento ou freezer.',
      'Usar material de merchandising e organizar exposição por categoria para aumentar leitura visual.'
    ],
    routine: [
      'Leitura de loja em 3 minutos antes da conversa.',
      'Conferir PPC, PPV, facing e concorrência na gôndola.',
      'Voltar em 24-48h se a entrega exigir proteção de execução.'
    ],
    proof: [
      'Mostrar fotos de execução e evidência de exposição.',
      'Levar dado de giro e saída da categoria quando disponível.',
      'Comparar ruptura evitada com potencial de faturamento preservado.'
    ]
  },
  externo: {
    priorities: [
      'Confirmar padrão, especificação, documentação e calendário de embarque antes de discutir preço.',
      'Reforçar regularidade, conformidade e histórico de entrega como parte da proposta.',
      'Amarrar vigência, volume e mix adicional quando houver concessão.'
    ],
    routine: [
      'Revisar forecast e disponibilidade antes de cada contato.',
      'Entrar na reunião com cenário de volume, calendário e risco logístico.',
      'Registrar toda negociação no CRM com condições e contrapartidas.'
    ],
    proof: [
      'Trazer histórico de regularidade e conformidade.',
      'Mostrar risco operacional e custo de troca de fornecedor.',
      'Conectar padrão e previsibilidade ao resultado do cliente.'
    ]
  }
};

export const SCENARIO_SIGNAL_LIBRARY: Array<{
  key: string;
  regex: RegExp;
  actionLabel: string;
  focusLabel: string;
}> = [
  { key: 'ruptura', regex: /ruptura|falta|sem estoque|abastecimento/, actionLabel: 'proteger abastecimento e execução', focusLabel: 'redução de ruptura' },
  { key: 'preco', regex: /preco|desconto|barato|verba|abaixo/, actionLabel: 'defender preço com valor', focusLabel: 'blindagem de margem' },
  { key: 'mix', regex: /mix|sortimento|portfolio|variedade/, actionLabel: 'ampliar mix com lógica de rentabilidade', focusLabel: 'mix produtivo' },
  { key: 'exposicao', regex: /gondola|exposicao|ilha|ponta|encarte|degustacao|planograma/, actionLabel: 'ganhar execução e visibilidade', focusLabel: 'melhor execução de PDV' },
  { key: 'share', regex: /share|espaco do concorrente|concorrente/, actionLabel: 'ganhar espaço estratégico', focusLabel: 'crescimento de participação' },
  { key: 'sazonal', regex: /sazonal|natal|pascoa|fim de ano|data forte/, actionLabel: 'capturar calendário comercial', focusLabel: 'janela sazonal' },
  { key: 'premium', regex: /premium|maior margem|valor agregado|ticket/, actionLabel: 'elevar rentabilidade da categoria', focusLabel: 'margem em reais' },
  { key: 'logistica', regex: /embarque|container|lead time|logistica|documentacao|forecast/, actionLabel: 'travar previsibilidade operacional', focusLabel: 'segurança de fornecimento' }
];
